// frontend/src/pages/client/ServicesClient.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import './ServicesClient.css';

const ICON_COLORS = [
  { bg: '#eff6ff', icon: '#2563eb' },
  { bg: '#ecfeff', icon: '#0891b2' },
  { bg: '#f0fdf4', icon: '#16a34a' },
  { bg: '#fdf4ff', icon: '#9333ea' },
  { bg: '#fff7ed', icon: '#ea580c' },
  { bg: '#fef2f2', icon: '#dc2626' },
  { bg: '#f0fdf4', icon: '#059669' },
  { bg: '#fafaf9', icon: '#57534e' },
];
const getColor = (name) => ICON_COLORS[(name?.charCodeAt(0) || 0) % ICON_COLORS.length];

const TABS = [
  { id: 'all',     label: 'Tous',           icon: 'fa-th-large' },
  { id: 'cheap',   label: 'Meilleurs prix', icon: 'fa-tag' },
  { id: 'popular', label: 'Populaires',     icon: 'fa-fire' },
  { id: 'stock',   label: 'En stock',       icon: 'fa-check-circle' },
];

const stagger = { visible: { transition: { staggerChildren: 0.07 } } };
const fadeUp  = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 22 } } };
const packFade = { hidden: { opacity: 0, y: 14 }, visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 22 } } };
const packStagger = { visible: { transition: { staggerChildren: 0.08 } } };

const ServicesClient = () => {
  const [services, setServices]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('all');
  const [search, setSearch]           = useState('');
  const [showSug, setShowSug]         = useState(false);
  const searchRef = useRef(null);

  // Modal state
  const [modalService, setModalService]   = useState(null);
  const [packs, setPacks]                 = useState([]);
  const [wallet, setWallet]               = useState(null);
  const [loadingPacks, setLoadingPacks]   = useState(false);
  const [purchasing, setPurchasing]       = useState(null);
  const [purchaseMsg, setPurchaseMsg]     = useState(null);
  const msgTimer = useRef(null);

  useEffect(() => { fetchServices(); }, []);

  useEffect(() => {
    const h = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSug(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Escape key closes modal
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = modalService ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [modalService]);

  const fetchServices = async () => {
    try {
      const res = await API.get('/client/services/all');
      setServices(res.data.services || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openModal = async (service) => {
    setModalService(service);
    setPacks([]);
    setWallet(null);
    setPurchaseMsg(null);
    setLoadingPacks(true);
    try {
      const [packsRes, walletsRes] = await Promise.all([
        API.get(`/client/packs/${service.id}`),
        API.get('/client/wallets'),
      ]);
      setPacks(packsRes.data.packs || []);
      const w = (walletsRes.data.wallets || []).find(w => w.company_id === service.id);
      setWallet(w || null);
    } catch { setPacks([]); }
    finally { setLoadingPacks(false); }
  };

  const closeModal = () => {
    setModalService(null);
    setPacks([]);
    setWallet(null);
    setPurchaseMsg(null);
    clearTimeout(msgTimer.current);
  };

  const showMsg = (type, text) => {
    setPurchaseMsg({ type, text });
    clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setPurchaseMsg(null), 5000);
  };

  const handlePurchase = async (pack) => {
    if (!modalService) return;
    setPurchasing(pack.id);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await API.post('/stripe/create-checkout-session', {
        pack_id:     pack.id,
        points:      pack.points_amount,
        amount:      pack.price,
        currency:    'eur',
        company_id:  modalService.id,
        user_id:     user.id,
        partner_id:  modalService.partner_id,
        type:        'client',
        success_url: `${window.location.origin}/dashboard/client?success=1`,
        cancel_url:  `${window.location.origin}/dashboard/client/services?canceled=1`,
      });
      if (res.data.url) window.location.href = res.data.url;
      else showMsg('error', 'URL de paiement manquante');
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Erreur lors du paiement');
    } finally { setPurchasing(null); }
  };

  const suggestions = (() => {
    const q = search.toLowerCase().trim();
    if (!q) return [];
    const seen = new Set(), out = [];
    services.forEach(s => {
      [{ v: s.name, t: 'Service' }, { v: s.partner_name, t: 'Partenaire' }].forEach(({ v, t }) => {
        if (v && v.toLowerCase().includes(q) && !seen.has(v)) { seen.add(v); out.push({ label: v, type: t }); }
      });
    });
    return out.slice(0, 6);
  })();

  const filtered = services.filter(s => {
    const q = search.toLowerCase().trim();
    const matchQ = !q || s.name.toLowerCase().includes(q) || (s.partner_name || '').toLowerCase().includes(q);
    const matchT =
      activeTab === 'cheap'   ? s.price_min <= 5 :
      activeTab === 'popular' ? s.packs_count >= 2 :
      activeTab === 'stock'   ? s.available_points > 0 : true;
    return matchQ && matchT;
  });

  if (loading) return (
    <div className="sc2-loading">
      <div className="sc2-spinner" />
      <p>Chargement des services…</p>
    </div>
  );

  return (
    <div className="sc2-page">

      {/* ── Hero ── */}
      <motion.div
        className="sc2-hero"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      >
        <div className="sc2-hero-orb sc2-hero-orb-1" />
        <div className="sc2-hero-orb sc2-hero-orb-2" />
        <div className="sc2-hero-left">
          <div className="sc2-hero-icon"><i className="fas fa-store" /></div>
          <div>
            <h1>Explorer les services</h1>
            <p>Découvrez les services disponibles et achetez vos points</p>
          </div>
        </div>
        <div className="sc2-hero-right">
          <div className="sc2-stat-pill">
            <i className="fas fa-cubes" />
            <span>{services.length} services disponibles</span>
          </div>
        </div>
      </motion.div>

      {/* ── Search + Tabs ── */}
      <motion.div
        className="sc2-card"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="sc2-toolbar">
          <div className="sc2-search-wrap" ref={searchRef}>
            <div className="sc2-search">
              <i className="fas fa-search" />
              <input
                placeholder="Rechercher un service ou partenaire…"
                value={search}
                onChange={e => { setSearch(e.target.value); setShowSug(true); }}
                onFocus={() => setShowSug(true)}
              />
              <AnimatePresence>
                {search && (
                  <motion.button className="sc2-clear" onClick={() => { setSearch(''); setShowSug(false); }}
                    initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}>
                    <i className="fas fa-times" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
            <AnimatePresence>
              {showSug && suggestions.length > 0 && (
                <motion.ul className="sc2-suggestions"
                  initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  {suggestions.map((s, i) => (
                    <li key={i} onMouseDown={() => { setSearch(s.label); setShowSug(false); }}>
                      <i className={`fas ${s.type === 'Partenaire' ? 'fa-building' : 'fa-cube'}`} />
                      <span>{s.label}</span>
                      <span className="sc2-sug-badge">{s.type}</span>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          <div className="sc2-tabs">
            {TABS.map(t => (
              <button key={t.id} className={`sc2-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
                <i className={`fas ${t.icon}`} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="sc2-count">
          <span>{filtered.length} service{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </motion.div>

      {/* ── Grid ── */}
      {filtered.length === 0 ? (
        <motion.div className="sc2-card sc2-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="sc2-empty-icon"><i className="fas fa-search" /></div>
          <h3>{search ? 'Aucun résultat' : 'Aucun service disponible'}</h3>
          <p>{search ? `Aucun service ne correspond à "${search}"` : 'Les partenaires n\'ont pas encore configuré de services'}</p>
        </motion.div>
      ) : (
        <motion.div className="sc2-grid" variants={stagger} initial="hidden" animate="visible">
          {filtered.map(service => {
            const color = getColor(service.name);
            const hasStock = service.available_points > 0;
            return (
              <motion.div
                key={service.id}
                className="sc2-service-card"
                variants={fadeUp}
                whileHover={{ y: -4, boxShadow: '0 16px 40px rgba(37,99,235,0.12)' }}
              >
                <div className="sc2-sc-header">
                  <div className="sc2-sc-icon" style={{ background: color.bg }}>
                    <i className="fas fa-cube" style={{ color: color.icon }} />
                  </div>
                  <div className="sc2-sc-meta">
                    <h3>{service.name}</h3>
                    {service.partner_name && (
                      <span className="sc2-sc-partner">
                        <i className="fas fa-building" /> {service.partner_name}
                      </span>
                    )}
                  </div>
                  <span className={`sc2-sc-stock ${hasStock ? 'ok' : 'low'}`}>
                    <i className="fas fa-circle" />
                    {hasStock ? 'En stock' : 'Rupture'}
                  </span>
                </div>

                {service.description && (
                  <p className="sc2-sc-desc">{service.description}</p>
                )}

                <div className="sc2-sc-stats">
                  <div className="sc2-sc-stat">
                    <i className="fas fa-boxes" style={{ color: '#2563eb' }} />
                    <span>{service.packs_count} pack{service.packs_count !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="sc2-sc-stat">
                    <i className="fas fa-tag" style={{ color: '#10b981' }} />
                    <span>Dès {service.price_min} {service.currency || 'TND'}</span>
                  </div>
                  <div className="sc2-sc-stat">
                    <i className="fas fa-coins" style={{ color: '#f59e0b' }} />
                    <span>{Number(service.points_min).toLocaleString()} pts min</span>
                  </div>
                </div>

                <div className="sc2-sc-stock-bar">
                  <div className="sc2-sc-stock-fill" style={{ width: hasStock ? '100%' : '0%' }} />
                </div>

                <motion.button
                  className="sc2-sc-btn"
                  onClick={() => hasStock && openModal(service)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  disabled={!hasStock}
                >
                  {hasStock
                    ? <><i className="fas fa-cart-plus" /> Voir les packs</>
                    : <><i className="fas fa-clock" /> Stock épuisé</>
                  }
                </motion.button>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* ── Packs Modal ── */}
      <AnimatePresence>
        {modalService && (
          <>
            {/* Backdrop */}
            <motion.div
              className="sc2-modal-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
            />

            {/* Centering wrapper (no transform conflict) */}
            <div className="sc2-modal-wrapper">
            {/* Panel */}
            <motion.div
              className="sc2-modal"
              initial={{ opacity: 0, y: 48, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            >
              {/* Header */}
              <div className="sc2-modal-header">
                <div className="sc2-modal-title">
                  <div className="sc2-modal-svc-icon" style={{ background: getColor(modalService.name).bg }}>
                    <i className="fas fa-cube" style={{ color: getColor(modalService.name).icon }} />
                  </div>
                  <div>
                    <h2>{modalService.name}</h2>
                    <p>
                      <i className="fas fa-building" /> {modalService.partner_name}
                      {wallet && (
                        <span className="sc2-modal-balance">
                          &nbsp;·&nbsp;<i className="fas fa-coins" /> {Number(wallet.balance).toLocaleString()} pts
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button className="sc2-modal-close" onClick={closeModal}>
                  <i className="fas fa-times" />
                </button>
              </div>

              {/* Alert */}
              <AnimatePresence>
                {purchaseMsg && (
                  <motion.div
                    className={`sc2-modal-alert sc2-modal-alert--${purchaseMsg.type}`}
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  >
                    <i className={`fas ${purchaseMsg.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
                    {purchaseMsg.text}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Body */}
              <div className="sc2-modal-body">
                {loadingPacks ? (
                  <div className="sc2-modal-loading">
                    <div className="sc2-spinner" />
                    <p>Chargement des packs…</p>
                  </div>
                ) : packs.length === 0 ? (
                  <div className="sc2-empty" style={{ padding: '48px 20px' }}>
                    <div className="sc2-empty-icon"><i className="fas fa-box-open" /></div>
                    <h3>Aucun pack disponible</h3>
                    <p>Le partenaire n'a pas encore configuré de packs pour ce service</p>
                  </div>
                ) : (
                  <>
                    <motion.div
                      className="sc2-modal-packs"
                      variants={packStagger}
                      initial="hidden"
                      animate="visible"
                    >
                      {packs.map((pack, i) => {
                        const isPopular = packs.length > 1 && i === Math.floor(packs.length / 2);
                        return (
                          <motion.div
                            key={pack.id}
                            className={`sc2-pack ${isPopular ? 'sc2-pack--popular' : ''}`}
                            variants={packFade}
                            whileHover={{ y: -5, boxShadow: '0 20px 48px rgba(37,99,235,0.15)' }}
                          >
                            {isPopular && (
                              <div className="sc2-popular-tag">
                                <i className="fas fa-star" /> Populaire
                              </div>
                            )}

                            <div className="sc2-pack-name">{pack.name}</div>

                            <div className="sc2-pack-pts">
                              <span className="sc2-pts-num">{Number(pack.points_amount).toLocaleString()}</span>
                              <span className="sc2-pts-unit">pts</span>
                            </div>

                            <div className="sc2-pack-price">
                              {pack.price} <span>{pack.currency}</span>
                            </div>

                            <div className="sc2-pack-rate">
                              ≈ {((pack.price / pack.points_amount) * 100).toFixed(2)} {pack.currency} / 100 pts
                            </div>

                            <ul className="sc2-pack-features">
                              <li><i className="fas fa-check" /> Points valables 1 an</li>
                              <li><i className="fas fa-check" /> Paiement Stripe sécurisé</li>
                              <li><i className="fas fa-check" /> Crédit instantané</li>
                            </ul>

                            <motion.button
                              className="sc2-buy-btn"
                              onClick={() => handlePurchase(pack)}
                              disabled={purchasing !== null}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.97 }}
                            >
                              {purchasing === pack.id
                                ? <><i className="fas fa-spinner fa-spin" /> Redirection…</>
                                : <><i className="fas fa-lock" /> Payer avec Stripe</>
                              }
                            </motion.button>
                          </motion.div>
                        );
                      })}
                    </motion.div>

                    <div className="sc2-modal-security">
                      <i className="fas fa-shield-alt" />
                      <span>Paiement 100% sécurisé · Chiffrement SSL · Aucune donnée bancaire stockée</span>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServicesClient;
